/*
 * Account navigation disclosure containing profile links and the sign-out action.
 * Used by AppNavigation for signed-in users.
 * Handles menu keys, outside clicks and focus return without rebuilding its trigger on routes.
 * Example: registerComponents(registry);
 *          <AccountMenu context={context} username={user.username} email={user.email}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { Icon } from '../../runtime/components/presentation.jsx';
import { positionNavigationMenu } from './menu-position.js';
import { registerComponents as registerSignOut, SignOut } from '../accounts/sign-out.jsx';

const destinations = [
  ['/me', 'Account'],
  ['/me/preferences', 'Preferences'],
  ['/me/library', 'Your library']
];

export class AccountMenu extends UIElement {
  static properties = {
    username: {},
    email: {},
    destination: {},
    open: { state: true },
    index: { state: true }
  };

  constructor() {
    super();
    this.username = 'Account';
    this.email = this.destination = '';
    this.open = false;
    this.index = -1;
    this.captureTrigger = (node) => (this.trigger = node);
    this.captureMenu = (node) => (this.menu = node);
    this.captureSignOut = (node) => (this.signOutControl = node);
    this.bindMethods('close');
  }

  updated(changed) {
    super.updated(changed);
    if (!this.context || !this.connection) return;
    if (this.attachedTo !== this.connection) {
      this.attachedTo = this.connection;
      const outside = this.connection.guard((event) => {
        if (!this.contains(event.target)) this.close();
      });
      const resize = this.connection.guard(() => {
        if (this.open) positionNavigationMenu(this.trigger, this.menu, this.context.window);
      });
      this.context.window.addEventListener('resize', resize);
      this.connection.own(() => this.context.window.removeEventListener('resize', resize));
      this.context.document.addEventListener('click', outside);
      this.connection.own(() => this.context.document.removeEventListener('click', outside));
      this.connection.own(() => this.context.window.clearTimeout(this.focusTimer));
    }
    if (this.open) positionNavigationMenu(this.trigger, this.menu, this.context.window);
  }

  get menuItems() {
    return [...this.menu.querySelectorAll('[data-account-menu-item]')];
  }

  async focusItem(index) {
    const total = destinations.length + 1;
    this.index = (index + total) % total;
    await this.whenRendered();
    if (!this.disposed && this.open) this.menuItems[this.index]?.focus();
  }

  openMenu(index = 0) {
    this.open = true;
    return this.focusItem(index);
  }

  close({ restoreFocus = false } = {}) {
    this.open = false;
    this.index = -1;
    if (restoreFocus && this.trigger?.isConnected) this.trigger.focus();
  }

  toggle() {
    return this.open ? this.close({ restoreFocus: true }) : this.openMenu();
  }

  triggerKey(event) {
    if (
      event.defaultPrevented ||
      event.isComposing ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey
    )
      return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      return this.openMenu(event.key === 'ArrowUp' ? destinations.length : 0);
    }
  }

  menuKey(event) {
    if (
      event.defaultPrevented ||
      event.isComposing ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    )
      return;
    if (event.key === 'Tab') {
      this.context.services.a11y.focusElement(this.trigger);
      this.close();
      return;
    }
    if (event.shiftKey) return;
    const active = this.context.document.activeElement;
    if (event.key === ' ' && active?.matches('a[data-account-menu-item]')) {
      event.preventDefault();
      active.click();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      return this.close({ restoreFocus: true });
    }
    const index =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? destinations.length
          : event.key === 'ArrowDown'
            ? this.index + 1
            : event.key === 'ArrowUp'
              ? this.index - 1
              : null;
    if (index !== null) {
      event.preventDefault();
      return this.focusItem(index);
    }
  }

  focusout() {
    this.context.window.clearTimeout(this.focusTimer);
    this.focusTimer = this.context.window.setTimeout(
      this.connection.guard(() => {
        if (!this.contains(this.context.document.activeElement)) this.close();
      }),
      0
    );
  }

  follow(event) {
    event.preventDefault();
    this.close();
    return this.context.window.navigate(event.currentTarget.getAttribute('href'));
  }

  render() {
    const links = destinations.map(([href, label], index) => (
      <a
        key={href}
        href={href}
        class="account-menu-item"
        role="menuitem"
        tabindex={this.index === index ? '0' : '-1'}
        data-account-menu-item=""
        aria-current={this.destination === href ? 'page' : null}
        onClick={this.follow}>
        {label}
      </a>
    ));
    return (
      <div id="account-menu-container" class="account-menu-container" onFocusout={this.focusout}>
        <button ref={this.captureTrigger} id="account-menu-button" type="button" class="nav-icon-button" title={`Account menu for ${this.username}`} aria-label="Account menu" aria-haspopup="menu" aria-expanded={String(this.open)} aria-controls="account-menu" onClick={this.toggle} onKeyDown={this.triggerKey}>
          <Icon name="user" classes="icon-sm"/>
        </button>
        <div ref={this.captureMenu} id="account-menu" class="account-menu-dropdown" hidden={!this.open} role="menu" aria-labelledby="account-menu-button" onKeyDown={this.menuKey}>
          <div class="account-menu-header" role="presentation">
            <strong id="account-menu-username">{this.username}</strong>
            <small id="account-menu-email">{this.email}</small>
          </div>
          {links}
          <div class="account-menu-footer" role="presentation">
            <SignOut
              ref={this.captureSignOut}
              buttonId="account-menu-signout"
              buttonClass="account-menu-item account-menu-signout"
              menuItem
              itemTabIndex={this.index === 3 ? 0 : -1}
              onStart={this.close}/>
          </div>
        </div>
      </div>
    );
  }
}

export function registerComponents(registry) {
  registerSignOut(registry);
  defineUIElements(registry, { 'tbc-account-menu': AccountMenu });
}
